import { InformationResponse as UtilizationInformationResponse } from '@/api-client/models/utilization/Contracts'
import { InformationResponse as BuildingScoreInformationResponse } from '@/api-client/models/buildingScore/Contracts'
import { InformationResponse as ClimateInformationResponse } from '@/api-client/models/climate/Contracts'
import { InformationResponse as ProjectInformationResponse } from '@/api-client/models/project/Contracts'
import { Ref, computed, isRef, onMounted, ref, unref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { CoreType } from '@/interfaces/core.interface'

type UtilizationInformation =
  | UtilizationInformationResponse
  | Ref<UtilizationInformationResponse | undefined>

type BuildingScoreInformation =
  | BuildingScoreInformationResponse
  | Ref<BuildingScoreInformationResponse | undefined>

type ClimateInformation = ClimateInformationResponse | Ref<ClimateInformationResponse | undefined>

type ProjectInformation = ProjectInformationResponse | Ref<ProjectInformationResponse | undefined>

type InformationType = 'utilisation' | 'buildingScore' | 'climate' | 'project'

export type ReportInformation =
  | {
      numberOf: Record<CoreType, number | undefined>
    }
  | {
      numberOfConstructions: number
      numberOfSpaces: number
    }

const useResultReportNew = (
  information: Ref<ReportInformation | undefined> | ReportInformation | undefined
) => {
  const { t } = useI18n()

  const constructions = computed(() => {
    const info = unref(information)
    if (info === undefined) return undefined
    if ('numberOfConstructions' in info) {
      return info.numberOfConstructions
    }
    if ('numberOf' in info) {
      return info.numberOf.CONSTRUCTION
    }
    return undefined
  })

  const spaces = computed(() => {
    const info = unref(information)
    if (info === undefined) return undefined
    if ('numberOfSpaces' in info) {
      return info.numberOfSpaces
    }
    if ('numberOf' in info && 'SPACE' in info.numberOf) {
      return info.numberOf.SPACE
    }
    return undefined
  })

  const report = computed(() => {
    const constructionsInfo = `${constructions.value ?? '-'} ${t(
      'general__data__building(s)'
    ).toLowerCase()}`
    const spacesInfo =
      spaces.value !== undefined
        ? `${spaces.value} ${t('general__text__room').toLowerCase()}`
        : undefined
    if (spacesInfo === undefined) {
      return `${t('general__text__summary')} ${constructionsInfo}`
    } else {
      return `${t('general__text__summary')} ${constructionsInfo} ${t(
        'general__text__and'
      ).toLowerCase()} ${spacesInfo}`
    }
  })

  const raw = computed(() => {
    return {
      constructions: constructions.value,
      spaces: spaces.value
    }
  })

  return { report, raw }
}

const useResultReport = <T extends InformationType>(
  informationType: T,
  information: T extends 'utilisation'
    ? UtilizationInformation
    : T extends 'buildingScore'
    ? BuildingScoreInformation
    : T extends 'climate'
    ? ClimateInformation
    : T extends 'project'
    ? ProjectInformation
    : never,
  isLoading: Ref<boolean>
) => {
  const { t } = useI18n()
  const result = ref<string>('')

  onMounted(() => {
    if (informationType === 'utilisation') {
      if (isRef(information)) {
        watch(
          information,
          (value) => {
            result.value = parseUsage(value as UtilizationInformationResponse | undefined)
          },
          { immediate: true }
        )
      } else {
        result.value = parseUsage(information as UtilizationInformationResponse)
      }
    }
    if (informationType === 'buildingScore') {
      if (isRef(information)) {
        watch(
          information,
          (value) => {
            result.value = parseBuildingScore(value as BuildingScoreInformationResponse | undefined)
          },
          { immediate: true }
        )
      } else {
        result.value = parseBuildingScore(information as BuildingScoreInformationResponse)
      }
    }
  })

  watch(
    isLoading,
    (value) => {
      if (value) {
        if (informationType === 'utilisation') {
          result.value = parseUsage(undefined)
        }
        if (informationType === 'buildingScore') {
          result.value = parseBuildingScore(undefined)
        }
      }
    },
    { immediate: true }
  )

  function parseBuildingScore(informationResponse?: BuildingScoreInformationResponse) {
    if (!informationResponse) {
      return t('general__text__summary') + ' - ' + t('general__data__building(s)').toLowerCase()
    }
    return (
      t('general__text__summary') +
      ' ' +
      informationResponse.numberOf.CONSTRUCTION +
      ' ' +
      t('general__data__building(s)').toLowerCase()
    )
  }
  function parseUsage(informationResponse?: UtilizationInformationResponse) {
    if (!informationResponse) {
      return (
        t('general__text__summary') +
        ' - ' +
        t('general__data__building(s)').toLowerCase() +
        ' ' +
        t('general__text__and').toLowerCase() +
        ' - ' +
        t('general__text__room').toLowerCase()
      )
    }
    return (
      t('general__text__summary') +
      ' ' +
      informationResponse.numberOfConstructions.toString() +
      ' ' +
      t('general__data__building(s)').toLowerCase() +
      ' ' +
      t('general__text__and').toLowerCase() +
      ' ' +
      informationResponse.numberOfSpaces.toString() +
      ' ' +
      t('general__text__room').toLowerCase()
    )
  }

  return { result }
}

export { useResultReport, useResultReportNew }
